export type DateFormatPreference = 'dd/MM/yyyy' | 'dd MMM yyyy' | 'yyyy-MM-dd';

const STORAGE_KEY = 'clinlexis_date_format';
const DEFAULT_FORMAT: DateFormatPreference = 'dd/MM/yyyy';

export function getDateFormatPreference(): DateFormatPreference {
  return (localStorage.getItem(STORAGE_KEY) as DateFormatPreference) || DEFAULT_FORMAT;
}

export function setDateFormatPreference(format: DateFormatPreference): void {
  localStorage.setItem(STORAGE_KEY, format);
}

function toDate(input: string | Date): Date {
  if (input instanceof Date) return input;
  const d = new Date(input);
  if (isNaN(d.getTime())) return new Date();
  return d;
}

const FORMATTERS: Record<DateFormatPreference, Intl.DateTimeFormat> = {
  'dd/MM/yyyy': new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }),
  'dd MMM yyyy': new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
  'yyyy-MM-dd': new Intl.DateTimeFormat('sv-SE', { year: 'numeric', month: '2-digit', day: '2-digit' }),
};

const DATETIME_FORMATTERS: Record<DateFormatPreference, Intl.DateTimeFormat> = {
  'dd/MM/yyyy': new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
  'dd MMM yyyy': new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
  'yyyy-MM-dd': new Intl.DateTimeFormat('sv-SE', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }),
};

/** Format a date string or Date to the user's preferred date format */
export function formatDate(input: string | Date): string {
  const pref = getDateFormatPreference();
  return FORMATTERS[pref].format(toDate(input));
}

/** Format a date+time string or Date to the user's preferred format with HH:mm */
export function formatDateTime(input: string | Date): string {
  const pref = getDateFormatPreference();
  return DATETIME_FORMATTERS[pref].format(toDate(input));
}

/** Smart relative date: "just now", "5 mins ago", "2 hours ago", "yesterday at 14:30", or full date */
export function formatSmartDateTime(input: string | Date): string {
  const date = toDate(input);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);

  if (diffMins < 1) return 'just now';
  if (diffMins === 1) return '1 min ago';
  if (diffMins < 60) return `${diffMins} mins ago`;
  if (diffHours === 1) return '1 hour ago';
  if (diffHours < 24) return `${diffHours} hours ago`;

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const TIME_FMT = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' });
  if (date.toDateString() === yesterday.toDateString()) {
    return `yesterday at ${TIME_FMT.format(date)}`;
  }

  return formatDateTime(input);
}

/** Preview a format using today's date (for the settings picker) */
export function previewFormat(format: DateFormatPreference): string {
  return FORMATTERS[format].format(new Date());
}
